import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import classNames from 'classnames';
import mapValues from 'lodash/mapValues';
import { useLayoutEffect, useMemo, useState } from 'react';
import { Responsive as ResponsiveGridLayout, } from 'react-grid-layout';
import { useMeasure } from 'react-use';
import { defaultBreakpointsDetails } from 'utils/mediaQuery/defaultConfig';
import { useDelayedState } from 'utils/useDelayedState';
import styles from './analyticsPanels.module.scss';
import { AnalyticsExportModal } from './components/export/components/modal/analyticsExportModal';
import { AnalyticsPanel } from './components/panel/analyticsPanel';
import { rglConfig } from './utils/theming/theming';
import { useAnalyticsState } from './utils/useAnalyticsState/useAnalyticsState';
import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';
export const AnalyticsPanels = ({ configurable = false, onChangeLayout, }) => {
    const { dashboard } = useAnalyticsState();
    const [gridRef, { width }] = useMeasure();
    const [isDragging, setIsDragging] = useState(false);
    const [isAnimated, setIsAnimated] = useDelayedState(false, 0);
    // By default, react-grid-layout component animates the layout on mount. We disable that by
    // setting `transition-property: none` on initial render using the `gridAnimated` class, and then
    // we enable it again immediately after the first render in the next event loop tick.
    useLayoutEffect(() => {
        setIsAnimated(true);
    });
    /**
     * The breakpoints object consisting of a breakpoint key and the screen width
     * where the breakpoint takes effect. The key names are arbitrary and they're
     * derived from the global breakpoint definitions, but they must match the
     * ones used in the `cols` and `layouts` values below for the grid to work.
     */
    const breakpoints = useMemo(() => defaultBreakpointsDetails.breakpoints.reduce((acc, { breakpoint, minWidth }) => (Object.assign(Object.assign({}, acc), { [breakpoint]: parseInt(minWidth, 10) })), {}), []);
    /**
     * The number of grid columns for each breakpoint key. For now, we don't
     * change the number of columns based on screen width.
     */
    const cols = useMemo(() => mapValues(breakpoints, () => rglConfig.cols), [breakpoints]);
    /**
     * The responsive layouts configurations. By default, we use the panel
     * `layout` defined in the API configuration. Where necessary, we override
     * those layouts according to what works best for each breakpoint.
     */
    const layouts = useMemo(() => {
        const defaultLayouts = dashboard.panels.map((panel) => (Object.assign({ i: panel.id }, panel.layout)));
        const responsiveLayouts = mapValues(breakpoints, (_value, key) => {
            switch (key) {
                // On smaller screens, we stack the panels vertically. Each panel gets a width of 100% and
                // the Y position is calculated based on the previous panel's height and Y position.
                case 'base':
                case 'sm':
                    return defaultLayouts.map((layout, i) => (Object.assign(Object.assign({}, layout), { w: rglConfig.cols, y: i > 0
                            ? defaultLayouts[i - 1].h + defaultLayouts[i - 1].y
                            : layout.y })));
                default:
                    return defaultLayouts;
            }
        });
        return responsiveLayouts;
    }, [breakpoints, dashboard]);
    return (_jsxs(_Fragment, { children: [_jsx("div", Object.assign({ ref: gridRef }, { children: _jsx(ResponsiveGridLayout, Object.assign({ width: width, className: classNames({
                        [styles.grid]: true,
                        [styles.gridAnimated]: isAnimated,
                        [styles.gridDragging]: isDragging,
                    }), layouts: layouts, breakpoints: breakpoints, cols: cols, margin: rglConfig.margin, containerPadding: rglConfig.containerPadding, rowHeight: rglConfig.rowHeight, isDraggable: configurable, isResizable: configurable, onDragStart: () => setIsDragging(true), onDragStop: () => setIsDragging(false), onLayoutChange: onChangeLayout }, { children: dashboard.panels.map((panel) => (_jsx("div", { children: _jsx(AnalyticsPanel, { panel: panel }) }, panel.id))) })) })), _jsx(AnalyticsExportModal, {})] }));
};
